/**
 * Knowledge Base V1 (migration 0086): wire shapes and the FIFTH route table.
 *
 * One shared set of how-to articles. A company's staff read the ones written for
 * them under Help; the operator reads all of them, writes them, and can have an
 * AI write them through the MCP server. Who sees what is decided by the API from
 * each article's `audience` and `status`, never by the client:
 *
 *   caller               sees
 *   -------------------  ---------------------------------------------------
 *   company staff        PUBLISHED articles whose audience is TENANT or BOTH.
 *                        A SUPER_ADMIN or draft article is a 404, even by slug.
 *   platform operator    everything, plus provenance (`operator` block).
 *   anyone else          refused (401 anonymous, 403 a signed-in non-staff).
 *
 * Why this is its own table and not `routes` / `PLATFORM_ROUTES`:
 *
 *  - Not in `routes`: those live under /t/:tenantSlug and are tenant-scoped. An
 *    article belongs to no tenant.
 *  - Not in `PLATFORM_ROUTES`: that table is deliberately never derived into MCP
 *    tools ("administering tenants is never an agent power"). Writing
 *    documentation IS a power the operator wants an AI to have, and it touches
 *    no tenant, so it gets its own table with its own rules rather than a hole
 *    in that one. Nothing in `PLATFORM_ROUTES` becomes reachable by this.
 *  - Reads are open to a company's staff, writes to the operator only. The MCP
 *    server hands a caller only the tools its `access` allows, and the API
 *    enforces the same thing; the knowledge route tests assert both.
 *
 * Markdown, no HTML. `[IMAGE: ...]` and `[VIDEO: ...]` are placeholders (see
 * knowledge-markdown.ts). An em-dash never reaches storage (engineering rule 12):
 * the API replaces one with a plain hyphen, the same guarantee Annie's `say()`
 * gives, because a prompt rule a model can quietly break is not a guarantee.
 */

import { z } from 'zod';

export const KNOWLEDGE_AUDIENCES = ['TENANT', 'SUPER_ADMIN', 'BOTH'] as const;
export const KnowledgeAudience = z.enum(KNOWLEDGE_AUDIENCES);
export type KnowledgeAudience = z.infer<typeof KnowledgeAudience>;

export const KNOWLEDGE_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export const KnowledgeStatus = z.enum(KNOWLEDGE_STATUSES);
export type KnowledgeStatus = z.infer<typeof KnowledgeStatus>;

export const KNOWLEDGE_SOURCES = ['HUMAN', 'AI'] as const;
export const KnowledgeSource = z.enum(KNOWLEDGE_SOURCES);
export type KnowledgeSource = z.infer<typeof KnowledgeSource>;

/** The headings a company's Help page groups articles under, in the order it lists them. */
export const KNOWLEDGE_TENANT_CATEGORIES = [
  'Getting Started',
  'Sales',
  'Consignments',
  'Lots & Catalog',
  'Bidders',
  'Ring Operations',
  'Payments & Checkout',
  'Load-Out',
  'Hardware',
  'Troubleshooting',
] as const;
/** For the operator's own notes. A company never sees an article filed here unless its audience says so. */
export const KNOWLEDGE_OPERATOR_CATEGORIES = ['Platform Admin', 'Operations & Runbooks', 'Architecture'] as const;
/**
 * The closed list a WRITE must use. It lives in code on purpose: a new category
 * is a one-line change, not a migration or a Categories page, and a model that
 * types "ring ops" cannot quietly split the Help page in two. Reads are lenient
 * (`category: z.string()`), so removing one later never breaks an old article.
 */
export const KNOWLEDGE_CATEGORIES = [...KNOWLEDGE_TENANT_CATEGORIES, ...KNOWLEDGE_OPERATOR_CATEGORIES] as const;
export const KnowledgeCategory = z.enum(KNOWLEDGE_CATEGORIES);
export type KnowledgeCategory = z.infer<typeof KnowledgeCategory>;

// -- slugs -------------------------------------------------------------------

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** True when a path segment is an article id rather than a slug (a slug may never look like one). */
export const looksLikeKnowledgeId = (segment: string): boolean => UUID_SHAPE.test(segment);

/** Lowercase words joined by single hyphens; never shaped like an id, so `:slug` can also take an id. */
export const KnowledgeSlug = z
  .string()
  .min(1)
  .max(100)
  .regex(SLUG, 'use lowercase letters, digits and single hyphens')
  .refine((s) => !UUID_SHAPE.test(s), 'a slug cannot look like an article id');

/** "Lots & Catalog: adding a lot" -> "lots-and-catalog-adding-a-lot". Never empty. */
export function knowledgeSlugFromTitle(title: string): string {
  const words = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 100)
    .replace(/-+$/, '');
  return words && !UUID_SHAPE.test(words) ? words : 'article';
}

// -- wire shapes -------------------------------------------------------------

/** What an article still needs: one box per unfilled `[IMAGE: ...]` / `[VIDEO: ...]`. */
export const KnowledgeMediaCounts = z.object({
  images: z.number().int().nonnegative(),
  videos: z.number().int().nonnegative(),
});

/**
 * Provenance and housekeeping for the operator only. Absent (not null) for a
 * company's staff: `source` and the two `...By` names are internal, and the
 * knowledge route tests assert none of it ever reaches a company.
 */
export const KnowledgeOperatorView = z.object({
  /** Who wrote the article first / who wrote it last. An AI edit runs on the operator's own credentials, so this is what tells the two apart. */
  source: KnowledgeSource,
  updatedSource: KnowledgeSource,
  createdBy: z.string(),
  updatedBy: z.string(),
  media: KnowledgeMediaCounts,
});
export type KnowledgeOperatorView = z.infer<typeof KnowledgeOperatorView>;

export const KnowledgeArticleSummary = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  audience: KnowledgeAudience,
  category: z.string(),
  status: KnowledgeStatus,
  sortOrder: z.number().int(),
  createdAt: z.string(),
  /** Any change to the article, publishing and unpublishing included. Shown to a company as "Last updated". */
  updatedAt: z.string(),
  /** Set exactly while the article is PUBLISHED. */
  publishedAt: z.string().nullable(),
  operator: KnowledgeOperatorView.optional(),
});
export type KnowledgeArticleSummary = z.infer<typeof KnowledgeArticleSummary>;

export const KnowledgeArticle = KnowledgeArticleSummary.extend({
  /** Plain Markdown with `[IMAGE: ...]` / `[VIDEO: ...]` placeholders. See knowledge-markdown.ts. */
  contentMarkdown: z.string(),
});
export type KnowledgeArticle = z.infer<typeof KnowledgeArticle>;

/** One page, whole articles only, and how many more there are (engineering rule 4). */
export const KnowledgeArticleList = z.object({
  items: z.array(KnowledgeArticleSummary),
  /** Every article that matched the filters. */
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  /** Matched but not in `items`. 0 means this page is the whole answer; otherwise ask again with a larger offset. */
  remaining: z.number().int().nonnegative(),
});
export type KnowledgeArticleList = z.infer<typeof KnowledgeArticleList>;

export const KnowledgeArticleDeleted = z.object({
  deleted: z.literal(true),
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
});
export type KnowledgeArticleDeleted = z.infer<typeof KnowledgeArticleDeleted>;

// -- requests ----------------------------------------------------------------

const Title = z.string().trim().min(1).max(200);
const Summary = z.string().trim().max(500);
const Content = z.string().max(100_000);
const SortOrder = z.number().int().min(0).max(10_000);

/** Always creates a DRAFT; publishing is its own call, so there is one way to go live. */
export const CreateKnowledgeArticleBody = z.object({
  title: Title,
  /** Defaults to the title, slugified. */
  slug: KnowledgeSlug.optional(),
  summary: Summary.optional(),
  contentMarkdown: Content.optional(),
  /** Defaults to TENANT. */
  audience: KnowledgeAudience.optional(),
  category: KnowledgeCategory,
  sortOrder: SortOrder.optional(),
});
export type CreateKnowledgeArticleBody = z.infer<typeof CreateKnowledgeArticleBody>;

/** Only what is sent changes. Status is not here: use publish / unpublish. */
export const UpdateKnowledgeArticleBody = z.object({
  title: Title.optional(),
  slug: KnowledgeSlug.optional(),
  summary: Summary.optional(),
  contentMarkdown: Content.optional(),
  audience: KnowledgeAudience.optional(),
  category: KnowledgeCategory.optional(),
  sortOrder: SortOrder.optional(),
});
export type UpdateKnowledgeArticleBody = z.infer<typeof UpdateKnowledgeArticleBody>;

/**
 * The query string of the list route. `audience` means "who can read it":
 * TENANT is what a company can read (TENANT and BOTH articles), SUPER_ADMIN is
 * what the operator console shows (SUPER_ADMIN and BOTH), BOTH is only the
 * shared ones. It only ever narrows: a company asking for SUPER_ADMIN gets the
 * BOTH articles it could read anyway, never anything else.
 *
 * `search` is a plain case-insensitive match over title, summary and content;
 * every word must appear somewhere in the article.
 */
export const KnowledgeListQuery = z.object({
  audience: KnowledgeAudience.optional(),
  category: z.string().max(60).optional(),
  status: KnowledgeStatus.optional(),
  search: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export type KnowledgeListQuery = z.infer<typeof KnowledgeListQuery>;

/** Page size when `limit` is not sent. */
export const KNOWLEDGE_DEFAULT_LIMIT = 50;

// -- the route table ---------------------------------------------------------

const NoBody = z.undefined();
const EmptyBody = z.object({}).strict();

export interface KnowledgeRouteDef<Req extends z.ZodTypeAny, Res extends z.ZodTypeAny> {
  readonly method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Absolute path - NOT prefixed by /t/:tenantSlug. `:name` segments are params. */
  readonly path: string;
  readonly request: Req;
  readonly response: Res;
  /**
   * 'reader': staff of any company, or the operator (what they then see is
   * still filtered by audience and status). 'operator': the platform operator
   * only. The server enforces it; the MCP server offers a caller only the
   * tools its access allows.
   */
  readonly access: 'reader' | 'operator';
  readonly summary: string;
}
const kr = <Req extends z.ZodTypeAny, Res extends z.ZodTypeAny>(d: KnowledgeRouteDef<Req, Res>) => d;

export const KNOWLEDGE_ROUTES = {
  listKnowledgeArticles: kr({
    method: 'GET', path: '/knowledge/articles', request: NoBody, response: KnowledgeArticleList, access: 'reader',
    summary: 'List or search knowledge articles (summaries, not the full text). Filters: audience, category, status, search. A company sees only published TENANT/BOTH articles; the operator sees all. Paged: check `remaining`.',
  }),
  getKnowledgeArticle: kr({
    method: 'GET', path: '/knowledge/articles/:slug', request: NoBody, response: KnowledgeArticle, access: 'reader',
    summary: 'One knowledge article in full, by slug (or by id). A company gets a 404 for anything it may not read, a SUPER_ADMIN or draft article included.',
  }),
  createKnowledgeArticle: kr({
    method: 'POST', path: '/knowledge/articles', request: CreateKnowledgeArticleBody, response: KnowledgeArticle, access: 'operator',
    summary: 'Create a knowledge article as a DRAFT. Operator only. An AI caller is recorded as the author (source AI).',
  }),
  updateKnowledgeArticle: kr({
    method: 'PATCH', path: '/knowledge/articles/:id', request: UpdateKnowledgeArticleBody, response: KnowledgeArticle, access: 'operator',
    summary: 'Change any of an article\'s title, slug, summary, content, audience, category or sort order. Only what is sent changes. Operator only. On a PUBLISHED article the change is live at once.',
  }),
  deleteKnowledgeArticle: kr({
    method: 'DELETE', path: '/knowledge/articles/:id', request: EmptyBody, response: KnowledgeArticleDeleted, access: 'operator',
    summary: 'Delete an article. Operator only. A copy of what was removed is kept in the audit trail. An AI caller may delete only a DRAFT.',
  }),
  publishKnowledgeArticle: kr({
    method: 'POST', path: '/knowledge/articles/:id/publish', request: EmptyBody, response: KnowledgeArticle, access: 'operator',
    summary: 'Publish a draft: companies whose audience matches can read it from now on. Operator only. Idempotent.',
  }),
  unpublishKnowledgeArticle: kr({
    method: 'POST', path: '/knowledge/articles/:id/unpublish', request: EmptyBody, response: KnowledgeArticle, access: 'operator',
    summary: 'Take a published article back to a draft: companies can no longer read it. Operator only. Idempotent.',
  }),
} as const;

export type KnowledgeRoutes = typeof KNOWLEDGE_ROUTES;
export type KnowledgeRouteName = keyof KnowledgeRoutes;
